/**
 * CommercialOutcomeEnquiriesDrawer — the drill-in behind the "Enquired"
 * commercial-outcome count on the Results page. Fetches the quote-grain rows
 * for one campaign+window on open and renders them as a list: date,
 * customer + linked contacts, quote description, status, value.
 *
 * Status is shown but never filtered — an open, won, or lost quote all count
 * as "enquired" (see campaign_commercial_outcomes_query.py), so this drawer
 * can never disagree with the count it opened from. A per-send row opens it
 * narrowed to the quotes credited to that send (attributed_step_id).
 */

import { useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignCommercialOutcomeEnquiry,
  CommercialOutcomeWindowDays,
  MarketingCategory,
} from "@shared-core/data/marketing-types";
import {
  describeProductScope,
  isProductScoped,
} from "@shared-core/domain/commercial-outcome-scope";
import { Badge } from "../../primitives/badge";
import {
  formatCurrencyGbp,
  formatDateOrDash,
} from "@shared-core/domain/formatters";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../primitives/sheet";

function statusVariant(status: string): "success" | "error" | "neutral" {
  if (status === "won") return "success";
  if (status === "lost") return "error";
  return "neutral";
}

interface CommercialOutcomeEnquiriesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: string;
  campaignName: string;
  windowDays: CommercialOutcomeWindowDays;
  /** The campaign's own product_keywords (campaign-product-keyword-outcomes),
   *  passed down from the outcome row that opened this drawer — null/empty
   *  means the backend already returned an unscoped quote list. */
  productKeywords: string[] | null;
  /** The campaign's own marketing_categories — quotes are scoped by their
   *  own category the same way. Null/empty means unscoped by category. */
  categories?: MarketingCategory[] | null;
  /** Narrows the list to the quotes credited to one send (a per-send row
   *  on the Results page), filtered client-side on attributed_step_id from
   *  the same fetched list — null shows the whole project. */
  send: { stepId: string; label: string } | null;
}

export function CommercialOutcomeEnquiriesDrawer({
  open,
  onOpenChange,
  campaignId,
  campaignName,
  windowDays,
  productKeywords,
  categories = null,
  send,
}: CommercialOutcomeEnquiriesDrawerProps) {
  const scope = { productKeywords, categories };
  const isScoped = isProductScoped(scope);
  const dataAdapter = useDataAdapter();
  const [allEnquiries, setAllEnquiries] = useState<
    CampaignCommercialOutcomeEnquiry[] | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(false);
      try {
        const res = await dataAdapter.getCampaignCommercialOutcomeEnquiries(
          campaignId,
          windowDays,
        );
        if (!cancelled) setAllEnquiries(res);
      } catch (e) {
        console.error(
          "marketing.results.commercial_outcome_enquiries.load.failed",
          e,
        );
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, open, campaignId, windowDays]);

  const enquiries =
    allEnquiries && send
      ? allEnquiries.filter((e) => e.attributed_step_id === send.stepId)
      : allEnquiries;
  const pricedTotal = (enquiries ?? []).reduce(
    (sum, e) => sum + (e.estimate_amt ?? 0),
    0,
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            Enquired
            {isScoped && <Badge variant="secondary">Product-scoped</Badge>}
          </SheetTitle>
          <SheetDescription>
            {campaignName}
            {send && <> · {send.label}</>} · {windowDays}-day window
            {isScoped && <> · counts: {describeProductScope(scope)}</>}
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 space-y-3 overflow-y-auto">
          {loading && (
            <div className="text-sm text-muted-foreground">
              Loading enquiries…
            </div>
          )}

          {error && !loading && (
            <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
              Couldn't load enquiries. Please try again.
            </div>
          )}

          {!loading && !error && enquiries && (
            <>
              {enquiries.length === 0 ? (
                <div className="text-sm p-8 text-center border border-border rounded-lg text-muted-foreground">
                  No enquiries to show.
                </div>
              ) : (
                <>
                  <div className="text-xs text-muted-foreground">
                    {enquiries.length} enquir
                    {enquiries.length === 1 ? "y" : "ies"}
                    {pricedTotal > 0 && (
                      <> · {formatCurrencyGbp(pricedTotal)} quoted total</>
                    )}
                  </div>
                  <ul className="space-y-2">
                    {enquiries.map((enquiry) => (
                      <li
                        key={enquiry.estimate_header_ref}
                        className="rounded-lg border border-border p-3 space-y-1.5"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-foreground">
                            {enquiry.customer_name ?? enquiry.customer_id}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {enquiry.estimate_amt !== null
                              ? formatCurrencyGbp(enquiry.estimate_amt)
                              : "Unpriced"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span>{formatDateOrDash(enquiry.estimate_date)}</span>
                          <Badge
                            variant={statusVariant(enquiry.status)}
                            className="capitalize"
                          >
                            {enquiry.status}
                          </Badge>
                        </div>
                        {enquiry.description && (
                          <div className="text-xs text-muted-foreground">
                            {enquiry.description}
                          </div>
                        )}
                        {enquiry.contacts.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            Linked contact
                            {enquiry.contacts.length > 1 ? "s" : ""}:{" "}
                            {enquiry.contacts
                              .map(
                                (c) =>
                                  c.name + (c.email ? ` (${c.email})` : ""),
                              )
                              .join(", ")}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
